import { sexSchema } from './shared.ts'
import * as z from 'zod'

export const registerSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
  termsOfUse: z.literal(true),
})

export const aboutSchema = z.object({
  image: z.file().or(z.undefined()).or(z.null()),
  firstname: z.string().min(1),
  lastname: z.string().min(1),
  birthdate: z.string().min(1),
  sex: sexSchema,
})

export const locationSchema = z.object({
  country: z.string().min(1),
  province: z.string().min(1),
  city: z.string().min(1),
  cp: z.string(),
})

export const personalInfoSchema = z.object({
  ...aboutSchema.shape,
  ...locationSchema.shape,
})

export const jobInfoSchema = z.object({
  job: z.string().min(1),
  work: z
    .object({
      workedPreviously: z.boolean(),
      company: z.string().nullable(),
      employees: z.number().nullable(),
    })
    .check((ctx) => {
      // `company == null` alone misses the empty string an input produces once it's been
      // focused and cleared, which let the step submit with a blank company. Treat
      // null/empty/whitespace as missing. The user-facing message comes from the centralized zod
      // error map (zod-i18n.ts) via the `i18n: 'required'` marker, so this schema carries no
      // translation function and can be exported directly.
      if (ctx.value.workedPreviously && !ctx.value.company?.trim()) {
        ctx.issues.push({ code: 'custom', path: ['company'], input: ctx.value, params: { i18n: 'required' } })
      }

      if (ctx.value.workedPreviously && ctx.value.employees == null) {
        ctx.issues.push({ code: 'custom', path: ['employees'], input: ctx.value, params: { i18n: 'required' } })
      }
    }),
  educationalLevel: z.number(),
  level: z.number(),
  function: z.number(),
  sector: z.number(),
})

export const applicantFormSchema = z.object({
  ...personalInfoSchema.shape,
  ...jobInfoSchema.shape,
})

const _schema = z.object({
  ...registerSchema.shape,
  ...personalInfoSchema.shape,
  ...jobInfoSchema.shape,
})

export type InitialData = Omit<Partial<z.input<typeof _schema>>, 'image'> & { image?: string | null }

// The persisted user fields the profile wizard collects across its steps.
export type ProfileUser = {
  image?: string | null
  firstname?: string | null
  lastname?: string | null
  birthdate?: string | null
  sex?: 'male' | 'female' | 'neutral' | null
  country?: string | null
  province?: string | null
  city?: string | null
  cp?: string | null
  job?: string | null
  company?: string | null
  employees?: number | null
  educationalLevel?: number | null
  level?: number | null
  function?: number | null
  sector?: number | null
}

// First wizard step (in order: about → location → career) whose data doesn't satisfy that step's
// own schema, or null when the profile is complete. Reusing the form schemas keeps the
// "is this step done?" rule in one place — the schemas themselves.
export function firstIncompleteProfileStep(user: ProfileUser): 'about' | 'location' | 'career' | null {
  // `image` is optional in the schema (the saved user has a CDN url, not a File), so omit it.
  if (!aboutSchema.safeParse({ image: undefined, ...pick(user, 'firstname', 'lastname', 'birthdate', 'sex') }).success)
    return 'about'

  if (!locationSchema.safeParse({ ...pick(user, 'country', 'province', 'city'), cp: user.cp ?? '' }).success)
    return 'location'

  // The user stores company/employees flat; the wizard nests them under `work` and only requires
  // them when `workedPreviously` is set — which is exactly when a company is on file.
  const career = {
    job: user.job,
    work: { workedPreviously: user.company != null, company: user.company ?? null, employees: user.employees ?? null },
    ...pick(user, 'educationalLevel', 'level', 'function', 'sector'),
  }
  if (!jobInfoSchema.safeParse(career).success) return 'career'

  return null
}

function pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> {
  return Object.fromEntries(keys.map((k) => [k, obj[k]])) as Pick<T, K>
}

export type ProfileStep = 'about' | 'location' | 'career'

// Field keys a profile step can render. `image`/`cp` are optional (never force a step); the rest are
// required by the step schemas. `work` is the worked-before toggle + its company/employees fields.
export type ProfileFieldKey =
  | 'image'
  | 'firstname'
  | 'lastname'
  | 'birthdate'
  | 'sex'
  | 'country'
  | 'province'
  | 'city'
  | 'cp'
  | 'job'
  | 'work'
  | 'educationalLevel'
  | 'level'
  | 'function'
  | 'sector'

const isFilled = {
  firstname: (u: ProfileUser) => !!u.firstname?.trim(),
  lastname: (u: ProfileUser) => !!u.lastname?.trim(),
  birthdate: (u: ProfileUser) => !!u.birthdate,
  sex: (u: ProfileUser) => u.sex != null,
  country: (u: ProfileUser) => !!u.country,
  province: (u: ProfileUser) => !!u.province,
  city: (u: ProfileUser) => !!u.city?.trim(),
  job: (u: ProfileUser) => !!u.job?.trim(),
  educationalLevel: (u: ProfileUser) => u.educationalLevel != null,
  level: (u: ProfileUser) => u.level != null,
  function: (u: ProfileUser) => u.function != null,
  sector: (u: ProfileUser) => u.sector != null,
}

/**
 * Which fields each profile step should render for `user`, OMITTING the ones already filled (e.g. by
 * an API/SDK prefill) so the wizard only asks for what's missing. Linked/dependent fields stay
 * together so a partial step still makes sense:
 *  - country + province (the province options depend on the chosen country),
 *  - the worked-before toggle + company/employees + professional level (level's options depend on the
 *    toggle), shown as a unit whenever the level is missing.
 * Optional fields (image, cp) are included only when their step is already shown for a required field.
 * A fully-complete step gets an empty list, so it can be dropped from both the wizard and the indicator.
 */
export function visibleProfileFields(user: ProfileUser): Record<ProfileStep, ProfileFieldKey[]> {
  const about: ProfileFieldKey[] = []
  if (!isFilled.firstname(user)) about.push('firstname')
  if (!isFilled.lastname(user)) about.push('lastname')
  if (!isFilled.birthdate(user)) about.push('birthdate')
  if (!isFilled.sex(user)) about.push('sex')
  if (about.length && !user.image) about.unshift('image') // optional → only when the step already shows

  const location: ProfileFieldKey[] = []
  if (!isFilled.country(user) || !isFilled.province(user)) location.push('country', 'province')
  if (!isFilled.city(user)) location.push('city')
  if (location.length && !user.cp) location.push('cp') // optional → only when the step already shows

  const career: ProfileFieldKey[] = []
  if (!isFilled.level(user)) career.push('work', 'level')
  if (!isFilled.job(user)) career.push('job')
  if (!isFilled.educationalLevel(user)) career.push('educationalLevel')
  if (!isFilled.function(user)) career.push('function')
  if (!isFilled.sector(user)) career.push('sector')

  return { about, location, career }
}

/** Profile steps that still have something to ask (in order) — for the wizard and the step indicator. */
export function neededProfileSteps(user: ProfileUser): ProfileStep[] {
  const fields = visibleProfileFields(user)
  return (['about', 'location', 'career'] as const).filter((step) => fields[step].length > 0)
}
